import moment from 'moment';

import { IDimensions, IItemMetrics, IRealtimeAnalytics } from '~/src/dash/api/console-api';
import {
  IDashboardChartData,
  IDashboardHomeCardMetrics,
  IDashboardLiveData,
} from '~/src/dash/api/dashboards/home';
import { createUrl, restGet } from '~/src/dash/api/rest';
import {
  calculateSmartFloorEstimatedRevenue,
  IRealtimeAnalyticsPayload,
  normalizeSmartFloorMetric,
} from '~/src/dash/apiV2/smartFloors/common';
import {
  AerospikeMetricKeys,
  createNewAggregator,
  GraphMetricKeys,
} from '~/src/dash/components/live-data/live-data';

const transformRealtimeAnalyticsToChartData = (
  realtimeData: IRealtimeAnalytics[],
): IDashboardLiveData => {
  const transformedLiveData = realtimeData.map((data) => {
    return {
      Date: data.DateExtended,

      BidFloorControl: Number(data.BidFloorControl.toFixed(2)),
      BidFloorOptimized: Number(data.BidFloorOptimized.toFixed(2)),
      BidFloorLow: Number(data.BidFloorLow.toFixed(2)),
      BidFloorHigh: Number(data.BidFloorHigh.toFixed(2)),

      WinningBidsControl: data.WinningBidsControl,
      WinningBidsOptimized: data.WinningBidsOptimized,
      WinningBidsLow: data.WinningBidsLow,
      WinningBidsHigh: data.WinningBidsHigh,

      CPMControl: data.CPMControl,
      CPMOptimized: data.CPMOptimized,
      CPMLow: data.CPMLow,
      CPMHigh: data.CPMHigh,

      BidResponsesControl: data.BidResponsesControl,
      BidResponsesOptimized: data.BidResponsesOptimized,
      BidResponsesLow: data.BidResponsesLow,
      BidResponsesHigh: data.BidResponsesHigh,

      BidRequestsOptimized: data.BidRequestsOptimized,
      BidRequestsControl: data.BidRequestsControl,
      BidRequestsLow: data.BidRequestsLow,
      BidRequestsHigh: data.BidRequestsHigh,

      EstimatedRevenueControl: Number(data.EstimatedRevenueControl.toFixed(2)),
      EstimatedRevenueOptimized: Number(data.EstimatedRevenueOptimized.toFixed(2)),
      EstimatedRevenueLow: Number(data.EstimatedRevenueLow.toFixed(2)),
      EstimatedRevenueHigh: Number(data.EstimatedRevenueHigh.toFixed(2)),
    } as IDashboardChartData;
  });
  return {
    LiveData: transformedLiveData,
    CardMetrics: {} as IDashboardHomeCardMetrics,
  };
};

const calculateAutomatedBidFloorDependentMetrics = (
  realtimeAnalytics: IRealtimeAnalytics[],
): IRealtimeAnalytics[] => {
  for (const analytic of realtimeAnalytics) {
    // BidFloor is stored on realtime as a sum of values just like any other metrics, hence
    // why we need to divide it by the number of requests to get the actual bid floor
    // We devide by 100 here cause realtime can't store floats, ex: 4.25 is stored as 425
    analytic.BidFloorOptimized =
      analytic.BidRequestsOptimized > 0
        ? analytic.BidFloorOptimized / (analytic.BidRequestsOptimized * 100)
        : 0;
    analytic.BidFloorLow =
      analytic.BidRequestsLow > 0 ? analytic.BidFloorLow / (analytic.BidRequestsLow * 100) : 0;
    analytic.BidFloorHigh =
      analytic.BidRequestsHigh > 0 ? analytic.BidFloorHigh / (analytic.BidRequestsHigh * 100) : 0;
    analytic.BidFloorControl =
      analytic.BidFloorControl > 0
        ? analytic.BidFloorControl / (analytic.BidRequestsControl * 100)
        : 0;

    // for backwards compatibility
    if (!analytic.CPMControl) {
      analytic.CPMControl = 0;
    }
    if (!analytic.WinningBidsControl) {
      analytic.WinningBidsControl = 0;
    }
    if (!analytic.BidRequestsControl) {
      analytic.BidRequestsControl = 0;
    }
    if (!analytic.BidResponsesControl) {
      analytic.BidResponsesControl = 0;
    }

    const totalBidRequests =
      analytic.BidRequestsOptimized +
      analytic.BidRequestsHigh +
      analytic.BidRequestsLow +
      analytic.BidRequestsControl;

    // only normalize estimated revenue
    const normalizedCPMOptimized = normalizeSmartFloorMetric(
      analytic.CPMOptimized,
      analytic.BidRequestsOptimized,
      totalBidRequests,
    );
    const normalizedCPMLow = normalizeSmartFloorMetric(
      analytic.CPMLow,
      analytic.BidRequestsLow,
      totalBidRequests,
    );
    const normalizedCPMHigh = normalizeSmartFloorMetric(
      analytic.CPMHigh,
      analytic.BidRequestsHigh,
      totalBidRequests,
    );
    const normalizedCPMControl = normalizeSmartFloorMetric(
      analytic.CPMControl,
      analytic.BidRequestsControl,
      totalBidRequests,
    );
    analytic.EstimatedRevenueOptimized =
      calculateSmartFloorEstimatedRevenue(normalizedCPMOptimized);
    analytic.EstimatedRevenueLow = calculateSmartFloorEstimatedRevenue(normalizedCPMLow);
    analytic.EstimatedRevenueHigh = calculateSmartFloorEstimatedRevenue(normalizedCPMHigh);
    analytic.EstimatedRevenueControl = calculateSmartFloorEstimatedRevenue(normalizedCPMControl);

    analytic.CPMOptimized =
      analytic.WinningBidsOptimized > 0
        ? analytic.CPMOptimized / (analytic.WinningBidsOptimized * 100)
        : analytic.CPMOptimized;
    analytic.CPMLow =
      analytic.WinningBidsLow > 0
        ? analytic.CPMLow / (analytic.WinningBidsLow * 100)
        : analytic.CPMLow;
    analytic.CPMHigh =
      analytic.WinningBidsHigh > 0
        ? analytic.CPMHigh / (analytic.WinningBidsHigh * 100)
        : analytic.CPMHigh;
    analytic.CPMControl =
      analytic.WinningBidsControl > 0
        ? analytic.CPMControl / (analytic.WinningBidsControl * 100)
        : analytic.CPMControl;
  }
  return realtimeAnalytics;
};

const aggregateAutomatedBidFloorRealtimeAnalytics = (
  data: IDimensions,
  endUnixTimestamp: number,
  startUnixTimestamp: number,
  itemKey: string,
  timezone: string,
): IRealtimeAnalytics[] => {
  const realtimeAnalytics = [] as IRealtimeAnalytics[];

  const minuteDifference = moment
    .unix(endUnixTimestamp)
    .diff(moment.unix(startUnixTimestamp), 'minutes');

  // get the metric keys for this resource
  const currentMetricKeys: { [key: string]: GraphMetricKeys } = {
    bid_won_cpm: 'CPM',
    bid_response: 'BidResponses',
    bid_request: 'BidRequests',
    bid_won: 'WinningBids',
    bid_floor: 'BidFloor',
  };

  const selectedMetricsKeys = Object.keys(currentMetricKeys) as AerospikeMetricKeys[];
  // calculate the number of periods based on minute difference and period duration
  // each period is 30 minute long
  const periodDuration = 30;
  const numPeriods = Math.ceil(minuteDifference / periodDuration);

  for (let period = 0; period < numPeriods; period++) {
    // create new aggregator and increment time stamp
    const realtimeAnalytic = createNewAggregator(startUnixTimestamp, timezone);
    startUnixTimestamp += 60 * 30;
    if (itemKey in data) {
      for (const [id, item] of Object.entries(data[itemKey].items)) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const metrics = item.metrics as IItemMetrics;
        for (const selectedMetric of selectedMetricsKeys) {
          if (selectedMetric in metrics) {
            const selectedMetricString = currentMetricKeys[selectedMetric];
            let metricString = selectedMetricString;
            if (id[id.length - 1] === '2') {
              metricString += 'Low';
            } else if (id[id.length - 1] === '1') {
              metricString += 'Optimized';
            } else if (id[id.length - 1] === '3') {
              metricString += 'High';
            } else if (id[id.length - 1] === '4') {
              metricString += 'Control';
            }

            if (metrics[selectedMetric].data) {
              const dataArray = metrics[selectedMetric].data;
              if (dataArray && selectedMetric in currentMetricKeys) {
                realtimeAnalytic[metricString] = dataArray[period];
              }
            }
          }
        }
      }
    }
    realtimeAnalytics.push(realtimeAnalytic);
  }
  return calculateAutomatedBidFloorDependentMetrics(realtimeAnalytics);
};

export async function getSmartFloorLiveData(
  publisherID: string,
  smartFloorID: number,
  beginTime: number,
  endTime: number,
  timezone: string,
): Promise<IDashboardLiveData> {
  const reqUrl = `${createUrl(
    '/realtime_analytics',
    publisherID,
    'v1',
  )}&begin_time=${beginTime}&end_time=${endTime}&automated_bid_floor_ids=${smartFloorID}&by_abf_period=true&metrics=3,9,12,73,75`;

  try {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const response = await restGet<IRealtimeAnalyticsPayload>(reqUrl, {});
    const realtimeAnalyticsData = aggregateAutomatedBidFloorRealtimeAnalytics(
      (response.parsedBody?.dimensions || {}) as IDimensions,
      endTime,
      beginTime,
      'automated_bidfloor_id_bidfloor_type',
      timezone,
    );

    const smartFloorsLiveData = transformRealtimeAnalyticsToChartData(realtimeAnalyticsData);
    return smartFloorsLiveData;
  } catch (e) {
    console.error(e);
    throw e;
  }
}
